import React from 'react';
import {findSequenceNeighbors, type RecordSeqEntry, type RecordSeqId} from '../lib/recordSequence.js';
import SequenceControl from './SequenceControl.js';

// Previous/Next record navigation (Design Constitution N08, O06). Renders
// nothing unless the current record sits inside a valid sequence handed
// through route state.
//
// The control itself — inline '‹ 3 of 12 ›', the target record in each step's
// aria-label/title, ←/→ outside fields, widgets and open modals — is the one
// shared SequenceControl. `layout` is kept for the callers that pass it; both
// values render the same inline control.

type FormatLabel = (item: RecordSeqEntry) => string;

type RecordSequenceNavProps = {
  seq: RecordSeqEntry[] | null | undefined;
  currentId: RecordSeqId;
  onNavigate: (id: RecordSeqId) => void;
  formatLabel?: FormatLabel;
  layout?: 'edge' | 'inline';
};

function defaultFormatLabel(item: RecordSeqEntry | null | undefined): string {
  if (!item) return '';
  if (item.label) return String(item.label);
  return item.tag ? '#' + String(item.tag) : 'Untagged';
}

export default function RecordSequenceNav({
  seq,
  currentId,
  onNavigate,
  formatLabel = defaultFormatLabel,
}: RecordSequenceNavProps) {
  const {index, total, prev, next} = findSequenceNeighbors(seq, currentId);
  const prevId = prev ? prev.id : null;
  const nextId = next ? next.id : null;
  const onPrev = React.useCallback(() => {
    if (prevId != null) onNavigate(prevId);
  }, [prevId, onNavigate]);
  const onNext = React.useCallback(() => {
    if (nextId != null) onNavigate(nextId);
  }, [nextId, onNavigate]);

  // No reliable sequence -> render nothing (direct link, notification, related
  // click-through, or single-record list).
  if (index === -1) return null;

  return (
    <SequenceControl
      index={index}
      total={total}
      onPrev={prevId != null ? onPrev : null}
      onNext={nextId != null ? onNext : null}
      prevLabel={prev ? formatLabel(prev) : ''}
      nextLabel={next ? formatLabel(next) : ''}
    />
  );
}
